import styled, { css } from 'styled-components';
import {
  Menu,
  MenuButton as BaseMenuButton,
  MenuItems,
  MenuPopover as BaseMenuPopover,
  MenuLink,
} from '@reach/menu-button';
import {
  Disclosure,
  DisclosureButton,
  DisclosurePanel,
} from '@reach/disclosure';
import { NavLink } from 'react-router-dom';
import { rgba } from 'polished';

import MenuOpenIcon from '#/assets/MenuOpenIcon';
import MenuCloseIcon from '#/assets/MenuCloseIcon';
import RoundedTriangleIcon from '#/assets/RoundedTriangleIcon';

import { c07071D, cFFFFFF } from '#/colors';

import { gradientUnderline } from '#/components/shared/styles';

import { useChainEnvsView } from '#/features/chain-envs/chain-envs-context';

import { PORTFOLIO_ROUTE, TRADE_ROUTE, STATUS_ROUTE } from '#/routes';
import {
  CODE_SAMPLES_URL,
  DISCORD_URL,
  PRODUCT_DOCS_URL,
  STATS_URL,
  TWITTER_URL,
  YOUTUBE_URL,
} from '#/routes-external';

interface Props {
  readonly className?: string;
}

export default function BurgerMenuNav(props: Props) {
  const { className } = props;
  const envsView = useChainEnvsView();

  return (
    <Menu>
      {({ isExpanded }) => (
        <>
          <BurgerMenuButton className={className}>
            {isExpanded ? <MenuCloseIcon /> : <MenuOpenIcon />}
          </BurgerMenuButton>

          <BurgerMenuPopover>
            <MenuItems>
              <MenuLink as={NavLink} to={TRADE_ROUTE}>
                Trade
              </MenuLink>
              <MenuLink as={NavLink} to={PORTFOLIO_ROUTE}>
                Portfolio
              </MenuLink>
              <Disclosure>
                <DisclosureButton>
                  Developers <Triangle />
                </DisclosureButton>
                <DisclosurePanel>
                  <MenuItems>
                    <MenuLink as={ExternalLink} href={PRODUCT_DOCS_URL}>
                      Docs
                    </MenuLink>
                    <MenuLink as={ExternalLink} href={API_DOCS_URL}>
                      API Docs
                    </MenuLink>
                    <MenuLink as={ExternalLink} href={CODE_SAMPLES_URL}>
                      Code Samples
                    </MenuLink>
                  </MenuItems>
                </DisclosurePanel>
              </Disclosure>
              <Disclosure>
                <DisclosureButton>
                  Community <Triangle />
                </DisclosureButton>
                <DisclosurePanel>
                  <MenuItems>
                    <MenuLink as={ExternalLink} href={DISCORD_URL}>
                      Discord
                    </MenuLink>
                    <MenuLink as={ExternalLink} href={TWITTER_URL}>
                      Twitter
                    </MenuLink>
                    <MenuLink as={ExternalLink} href={YOUTUBE_URL}>
                      YouTube
                    </MenuLink>
                  </MenuItems>
                </DisclosurePanel>
              </Disclosure>
              <Disclosure>
                <DisclosureButton>
                  Environments <Triangle />
                </DisclosureButton>
                <DisclosurePanel>
                  <MenuItems>
                    {envsView.options.map((env) => (
                      <MenuLink
                        key={env.id}
                        href={env.url}
                        disabled={env.id === envsView.current.id}
                      >
                        {env.name}
                      </MenuLink>
                    ))}
                  </MenuItems>
                </DisclosurePanel>
              </Disclosure>
              <MenuLink as={ExternalLink} href={STATS_URL}>
                Stats
              </MenuLink>
              <MenuLink as={ExternalLink} href={STATUS_ROUTE}>
                System Status
              </MenuLink>
            </MenuItems>
          </BurgerMenuPopover>
        </>
      )}
    </Menu>
  );
}

const BurgerMenuButton = styled(BaseMenuButton)`
  width: 32px;
  height: 32px;
  padding: 0;
  border: none;
  background: none;
`;

const navItemStyles = css`
  color: ${rgba(cFFFFFF, 0.5)};
  font-size: 14px;
  line-height: 14px;
  padding: 16px 0;
  margin: 4px 0;
  font-weight: 700;

  &:active {
    color: ${cFFFFFF};
  }
`;

const Triangle = styled(RoundedTriangleIcon)``;

const BurgerMenuPopover = styled(BaseMenuPopover)`
  background-color: ${c07071D};
  width: 100vw;
  min-height: 100%;

  &[style] {
    left: 0 !important;
    top: 60px !important;
  }

  [data-reach-menu-items] {
    display: grid;
    justify-items: start;
    align-items: center;
    margin: 12px 16px;

    /* App nav links */
    & > [data-reach-menu-link] {
      ${navItemStyles}

      &.active {
        color: ${cFFFFFF};
        ${gradientUnderline}

        &:after {
          bottom: 10px;
        }
      }
    }

    /* High-level collapsible items */
    [data-reach-disclosure-button] {
      ${navItemStyles}
      align-items: center;
      background: none;
      border: none;
      display: grid;
      gap: 4px;
      grid-auto-flow: column;

      ${Triangle} {
        display: inline-block;
        position: relative;
        top: 2px;
      }

      &[data-state='open'] {
        color: ${cFFFFFF};
        margin-bottom: 0;

        ${Triangle} {
          transform: rotate(180deg);
        }
      }
    }

    /* Submenu */
    [data-reach-disclosure-panel] {
      [data-reach-menu-items] {
        margin: 0px 0px 4px 0px;

        & > [data-reach-menu-link] {
          padding: 8px 0;
          margin: 0;

          &[data-disabled] {
            color: ${cFFFFFF};
          }
        }
      }
    }
  }
`;

const ExternalLink = styled.a.attrs({
  target: '_blank',
  rel: 'noopener',
})``;
